<template>
  <div class="h-screen bg-gray-50 flex flex-col">
    <!-- 统计卡片 -->
    <div class="flex-shrink-0 bg-white p-2 mb-1 shadow-sm">
      <!-- 统计卡片 -->
      <div class="flex items-center justify-between mb-2">
        <div class="grid grid-cols-3 gap-2 flex-1">
          <div class="text-center">
            <p class="text-lg font-bold text-blue-600">{{ statistics.totalQuantity || 0 }}</p>
            <p class="text-xs text-gray-500">库存总量</p>
          </div>
          <div class="text-center">
            <p class="text-lg font-bold text-green-600">¥{{ formatAmount(statistics.totalAmount) }}</p>
            <p class="text-xs text-gray-500">库存总值</p>
          </div>
          <div class="text-center">
            <p class="text-lg font-bold text-red-600">{{ statistics.warningCount || 0 }}</p>
            <p class="text-xs text-gray-500">库存预警</p>
          </div>
        </div>
        <!-- 快捷操作菜单按钮 -->
        <div class="relative ml-2">
          <button @click="showQuickActions = !showQuickActions"
            class="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>
          <!-- 快捷操作弹出菜单 -->
          <div v-if="showQuickActions" 
            class="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 min-w-[120px]">
            <button @click="handleQuickAction('/mobile/inventory/check')"
              class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 rounded-t-lg">
              <svg class="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              <span>库存盘点</span>
            </button>
            <button @click="handleQuickAction('/mobile/inventory/adjust')"
              class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 rounded-b-lg">
              <svg class="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span>库存调整</span>
            </button>
          </div>
        </div>
      </div>

      <!-- 筛选和搜索 -->
      <div class="flex items-center space-x-3">
        <div class="flex-1">
          <input v-model="searchParams.productName" type="text" placeholder="搜索商品名称、编码..."
            class="w-full px-3 py-2 border-0 bg-gray-50 rounded-lg focus:ring-2 focus:ring-green-500 focus:bg-white focus:outline-none"
            @input="handleSearchDebounced" />
        </div>
        <button @click="handleSearch" :disabled="searching"
          class="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50">
          <svg v-if="searching" class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor"
              d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
            </path>
          </svg>
          <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="m21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button>
        <button @click="showFilter = !showFilter"
          class="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
        </button>
      </div>

      <!-- 筛选选项 -->
      <div v-if="showFilter" class="border-t border-gray-200 pt-3">
        <div class="grid grid-cols-2 gap-3 mb-3">
          <select v-model="searchParams.category" class="px-3 py-2 border border-gray-300 rounded-lg"
            @change="handleSearch">
            <option value="">全部分类</option>
            <option value="electronics">电子产品</option>
            <option value="clothing">服装</option>
            <option value="food">食品</option>
            <option value="books">图书</option>
          </select>

          <select v-model="searchParams.stockStatus" class="px-3 py-2 border border-gray-300 rounded-lg"
            @change="handleSearch">
            <option value="">全部状态</option>
            <option value="normal">库存正常</option>
            <option value="warning">库存预警</option>
            <option value="stockout">缺货</option>
          </select>
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div class="flex items-center space-x-2">
            <input v-model.number="searchParams.minStock" type="number" min="0" placeholder="最小库存(默认1)"
              class="px-3 py-2 border border-gray-300 rounded-lg w-full" @input="handleSearchDebounced" />
          </div>
          <div class="flex items-center space-x-2">
            <input v-model.number="searchParams.maxStock" type="number" min="0" placeholder="最大库存(留空不限)"
              class="px-3 py-2 border border-gray-300 rounded-lg w-full" @input="handleSearchDebounced" />
          </div>
        </div>
        <!-- <div class="grid grid-cols-2 gap-3 mt-3">
          <button
            @click="resetSearch"
            class="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
          >
            重置
          </button>
          <button
            @click="applyDefaultStockFilter"
            class="px-3 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
          >
            默认: >0
          </button>
        </div> -->
      </div>
    </div>

    <!-- 内容区域 -->
    <div class="flex-1 overflow-y-auto px-1 pb-8">

      <!-- 库存预警 -->
      <!-- <div v-if="lowStockItems.length > 0" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 mx-4">
      <div class="flex items-center mb-2">
        <svg class="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <h3 class="text-sm font-semibold text-red-800">库存预警</h3>
      </div>
      <div class="space-y-2">
        <div 
          v-for="item in lowStockItems.slice(0, 3)" 
          :key="item.id"
          class="flex items-center justify-between text-sm"
        >
          <span class="text-red-700">{{ item.productName }}</span>
          <span class="text-red-600 font-medium">剩余 {{ item.stockQuantity }}</span>
        </div>
        <button
          v-if="lowStockItems.length > 3"
          @click="showAllLowStock = !showAllLowStock"
          class="text-red-600 text-sm hover:text-red-700 transition-colors"
        >
          {{ showAllLowStock ? '收起' : `查看全部 ${lowStockItems.length} 项` }}
        </button>
      </div>
    </div> -->


      <!-- 库存列表 -->
      <div>
        <div v-if="loading" class="text-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          <p class="text-gray-500 mt-2">加载中...</p>
        </div>

        <div v-else-if="tableData.length === 0" class="text-center py-8">
          <svg class="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          <p class="text-gray-500">暂无库存记录</p>
        </div>

        <div v-else class="space-y-1">
          <div v-for="item in tableData" :key="item.id"
            class="bg-white rounded-lg p-2 shadow-sm border border-gray-100 hover:shadow-md transition-shadow mx-1"
            @click="viewItemDetail(item)">
            <div class="flex items-start justify-between mb-2">
              <div class="flex-1 flex items-center gap-2">
                <img v-if="item.productImageUrl" :src="item.productImageUrl.trim()"
                  class="w-10 h-10 object-cover rounded" :alt="item.productName" />
                <div>
                  <h4 class="font-semibold text-gray-900 text-sm">{{ item.productName }}</h4>
                  <p class="text-xs text-gray-500">编码: {{ item.productSku }}</p>
                </div>
              </div>
              <span :class="[
                'px-1 py-0.5 rounded-full text-xs font-medium',
                getStockStatusClass(getStockStatus(item.stockQuantity))
              ]">
                {{ getStockStatusText(getStockStatus(item.stockQuantity)) }}
              </span>
            </div>

            <div class="grid grid-cols-3 gap-2 text-xs mb-2">
              <div>
                <p class="text-gray-500">当前库存</p>
                <p :class="[
                  'font-semibold',
                  getStockStatus(item.quantity) === 'warning' ? 'text-orange-600' :
                    getStockStatus(item.quantity) === 'stockout' ? 'text-red-600' : 'text-gray-900'
                ]">
                  {{ item.quantity }}
                </p>
              </div>
              <div>
                <p class="text-gray-500">销售价</p>
                <p class="font-semibold text-green-900">{{ item.salePrice || 0 }}</p>
              </div>
              <div>
                <p class="text-gray-500">成本价</p>
                <p class="font-semibold text-blue-900">¥{{ formatAmount(item.costPrice || 0) }}</p>
              </div>
            </div>

            <!-- 库存进度条 -->
            <!-- <div class="mt-3">
            <div class="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span>库存水位</span>
              <span>{{ Math.round((item.stockQuantity / Math.max(item.warningQuantity * 2, 20)) * 100) }}%</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div 
                :class="[
                  'h-2 rounded-full transition-all duration-300',
                  getStockStatus(item.stockQuantity) === 'stockout' ? 'bg-red-500' :
                  getStockStatus(item.stockQuantity) === 'warning' ? 'bg-orange-500' : 'bg-green-500'
                ]"
                :style="{ width: `${Math.min((item.stockQuantity / Math.max(item.warningQuantity * 2, 20)) * 100, 100)}%` }"
              ></div>
            </div>
          </div> -->

            <div class="flex items-center justify-between pt-2 border-t border-gray-100">
              <p class="text-xs text-gray-500">
                库存总值: ¥{{ formatAmount(item.costPrice * item.quantity) }}
              </p>
              <div class="flex items-center space-x-1">
                <button @click.stop="adjustStock(item)"
                  class="px-2 py-0.5 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors">
                  调整
                </button>
                <button @click.stop="viewHistory(item)"
                  class="px-2 py-0.5 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 transition-colors">
                  历史
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- 移动端分页组件 - 固定在底部 -->
    <div v-if="!loading && tableData.length > 0" class="fixed bottom-16 left-0 right-0 bg-white border-gray-200 shadow-lg z-40 px-2 py-1">
      <MobilePager 
        :current-page="pagination.current"
        :total-pages="Math.ceil(pagination.total / pagination.pageSize)"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        :show-size-changer="false"
        :show-quick-jumper="false"
        @update:current-page="handlePageChange"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { inventoryApi } from '@/api/api'
import MobilePager from '@/mobile/components/MobilePager.vue'

const router = useRouter()

const loading = ref(false)
const searching = ref(false)
const showFilter = ref(false)
const showQuickActions = ref(false)


// 统计数据
const statistics = ref({
  totalQuantity: 0,
  totalAmount: 0,
  productTypes: 0,
  warningCount: 0
})

// 搜索参数
const searchParams = ref({
  productName: '',
  productCode: '',
  // warehouseName: '',
  stockStatus: '',
  category: '',
  minStock: 1,
  maxStock: ''
})

// 表格数据
const tableData = ref([])

// 分页信息
const pagination = ref({
  total: 0,
  current: 1,
  pageSize: 20
})

// 搜索防抖
let searchTimeout = null
const handleSearchDebounced = () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    handleSearch()
  }, 500)
}



// 格式化金额
const formatAmount = (amount) => {
  return Number(amount || 0).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

// 获取库存状态
const getStockStatus = (quantity) => {
  if (quantity <= 0) return 'stockout'
  if (quantity <= 10) return 'warning'
  return 'normal'
}

// 获取库存状态样式
const getStockStatusClass = (status) => {
  const classes = {
    normal: 'bg-green-100 text-green-800',
    warning: 'bg-orange-100 text-orange-800',
    stockout: 'bg-red-100 text-red-800'
  }
  return classes[status] || 'bg-gray-100 text-gray-800'
}

// 获取库存状态文本
const getStockStatusText = (status) => {
  const texts = {
    normal: '库存正常',
    warning: '库存预警',
    stockout: '缺货'
  }
  return texts[status] || '未知'
}

// 获取统计数据
const fetchStatistics = async () => {
  try {
    const res = await inventoryApi.getInventoryStatistics()
    if (res.success) {
      const data = res.data
      statistics.value = {
        totalQuantity: data.totalQuantity || 0,
        totalAmount: data.totalAmount || 0,
        productTypes: data.productTypes || 0,
        warningCount: data.warningCount || 0
      }
    }
  } catch (error) {
    console.error('获取库存统计数据失败:', error)
    statistics.value = {
      totalQuantity: 0,
      totalAmount: 0,
      productTypes: 0,
      warningCount: 0
    }
  }
}

// 获取列表数据
const fetchData = async () => {
  try {
    loading.value = true

    const params = {
      pagination: {
        current: pagination.value.current,
        pageSize: pagination.value.pageSize
      },
      search: {
        productName: searchParams.value.productName,
        productCode: searchParams.value.productCode,
        // warehouseName: searchParams.value.warehouseName,
        stockStatus: searchParams.value.stockStatus,
        category: searchParams.value.category,
        minStock: searchParams.value.minStock === '' ? null : Number(searchParams.value.minStock),
        maxStock: searchParams.value.maxStock === '' ? null : Number(searchParams.value.maxStock)
      },
      sort: {
        predicate: "createdOn",
        reverse: true
      }
    }

    const res = await inventoryApi.getInventoryList(params)
    if (res.success) {
      const result = res.data
      const newData = result.list.map(item => ({
        ...item,
        stockStatus: getStockStatus(item.stockQuantity)
      }))

      tableData.value = newData

      pagination.value = {
        total: result.pagination?.total || result.total || 0,
        current: result.pagination?.current || result.current || pagination.value.current,
        pageSize: result.pagination?.pageSize || result.pageSize || pagination.value.pageSize
      }
    }
  } catch (error) {
    console.error('获取库存列表失败:', error)
  } finally {
    loading.value = false
  }
}

// 搜索
const handleSearch = async () => {
  try {
    searching.value = true
    pagination.value.current = 1 // 重置到第一页
    await fetchData()
    await fetchStatistics()
  } finally {
    searching.value = false
  }
}

// 应用默认 >0 过滤
const applyDefaultStockFilter = () => {
  searchParams.value.minStock = 1
  searchParams.value.maxStock = ''
  pagination.value.current = 1 // 重置到第一页
  fetchData()
  fetchStatistics()
}

// 分页处理
const handlePageChange = async (page) => {
  // 立即设置loading状态并清空当前数据
  loading.value = true
  tableData.value = []
  pagination.value.current = page
  
  // 获取新数据
  await fetchData()
}

const viewItemDetail = (item) => {
  // 可以添加详情页面
  console.log('查看详情:', item)
}

const adjustStock = (item) => {
  // 库存调整功能
  console.log('调整库存:', item)
}

const viewHistory = (item) => {
  router.push(`/mobile/inventory/history/${item.productId}`)
}

// 快捷操作处理
const handleQuickAction = (path) => {
  showQuickActions.value = false
  router.push(path)
}

onMounted(() => {
  // 默认展示库存>0
  applyDefaultStockFilter()
})
</script>

<style scoped>
/* 库存进度条动画 */
.bg-green-500,
.bg-orange-500,
.bg-red-500 {
  transition: width 0.3s ease-in-out;
}

/* 确保卡片悬停效果 */
.hover\:shadow-md:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}
</style>